import React from "react";
import PropTypes from "prop-types";

import useMediaQuery from "@mui/material/useMediaQuery";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import { MIN_DESKTOP_WIDTH } from "../../../constants/ui";
import { moment } from "../../../utils/LocalizedUtils";

import {
  useDefaultPasscodeStyles,
  textfield,
  switchControl,
  selectField,
  getSelectValues,
  getKeyFromSelectedValue,
  getSelectedValueTranslated,
} from "../../../constants/passCodeUtils";

import {
  CONFERENCE_TYPE,
  LANGUAGE,
  MUSICSOURCE,
  CALL_FLOW,
  CUSTOM_NAME,
  CONFERENCE_ID,
  SCHEDULED_DATE,
  SCHEDULED_DURATION,
  CONFERENCE_NAME,
  MAX_PARTICIPANTS,
  HOST_PASSCODE,
  GUEST_PASSCODE,
  CHAIR_DISCONNECT,
  OWNER_EMAIL,
  OWNER,
  COMMENT,
} from "../../../utils/responseValue";

const readOnlyFieldSx = {
  backgroundColor: "#ffffff",
  borderRadius: "5px",
};

const PMConference = ({ confRestRep, setConfRestRep, selectValues, translate, requestNewPin }) => {
  const pc = useDefaultPasscodeStyles();
  const isDesktop = useMediaQuery(`(min-width:${MIN_DESKTOP_WIDTH})`);

  const handleTextChange = name => event => {
    setConfRestRep({ ...confRestRep, [name]: event.target.value });
  };
  const handleSwitchChange = name => event => {
    setConfRestRep({ ...confRestRep, [name]: event.target.checked });
  };
  const handleSelectChange = name => event => {
    setConfRestRep({
      ...confRestRep,
      [name]: getKeyFromSelectedValue(selectValues[name], event.target.value),
    });
  };

  const handleDateChange = (name, date) => {
    setConfRestRep({
      ...confRestRep,
      [name]: moment.utc(date).valueOf(),
    });
  };

  const customStyle = theme => ({
    formControl: {
      width: "200px",
    },
  });

  const centererButtonSx = { width: "50%", textAlign: "center" };

  return (
    <Box component="form" sx={pc.container} noValidate autoComplete="off">
      <Box sx={pc.centerer}>
        <TextField
          label={translate[OWNER]}
          sx={[pc.textField, isDesktop ? pc.lineExpander : null].filter(Boolean)}
          value={confRestRep[OWNER]}
          onChange={handleTextChange(OWNER)}
          margin="normal"
        />
        <TextField
          label={translate[CONFERENCE_ID]}
          sx={[pc.textField, isDesktop ? pc.lineExpander : null, readOnlyFieldSx].filter(Boolean)}
          value={confRestRep[CONFERENCE_ID] || ""}
          margin="normal"
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
        />
      </Box>
      <Box sx={pc.lineBreak} />
      <Box sx={pc.centerer}>
        {selectField(
          customStyle,
          translate[CONFERENCE_TYPE],
          getSelectValues(selectValues, CONFERENCE_TYPE),
          getSelectedValueTranslated(confRestRep[CONFERENCE_TYPE], selectValues, CONFERENCE_TYPE),
          handleSelectChange(CONFERENCE_TYPE),
        isDesktop
        )}
        {selectField(
          customStyle,
          translate[LANGUAGE],
          getSelectValues(selectValues, LANGUAGE),
          getSelectedValueTranslated(confRestRep[LANGUAGE], selectValues, LANGUAGE),
          handleSelectChange(LANGUAGE),
        isDesktop
        )}
        {selectField(
          customStyle,
          translate[MUSICSOURCE],
          getSelectValues(selectValues, MUSICSOURCE),
          getSelectedValueTranslated(confRestRep[MUSICSOURCE], selectValues, MUSICSOURCE),
          handleSelectChange(MUSICSOURCE),
        isDesktop
        )}
        {selectField(
          customStyle,
          translate[CALL_FLOW],
          getSelectValues(selectValues, CALL_FLOW),
          getSelectedValueTranslated(confRestRep[CALL_FLOW], selectValues, CALL_FLOW),
          handleSelectChange(CALL_FLOW),
        isDesktop
        )}
      </Box>
      <Box sx={pc.lineBreak} />
      {textfield(pc, isDesktop, confRestRep[CUSTOM_NAME], translate[CUSTOM_NAME], handleTextChange(CUSTOM_NAME))}
      {textfield(pc, isDesktop, confRestRep[OWNER_EMAIL], translate[OWNER_EMAIL], handleTextChange(OWNER_EMAIL))}
      <Box sx={pc.lineBreak} />
      <Box sx={{ pt: 2 }}>
        <DateTimePicker
          ampm={false}
          format="DD.MM.YYYY HH:mm:ss"
          value={confRestRep[SCHEDULED_DATE] ? moment(confRestRep[SCHEDULED_DATE]) : null}
          onChange={date => handleDateChange(SCHEDULED_DATE, date)}
          slotProps={{
            textField: {
              label: translate[SCHEDULED_DATE],
              sx: pc.textField,
            },
          }}
        />
      </Box>
      {textfield(
        pc,
        isDesktop,
        confRestRep[SCHEDULED_DURATION],
        translate[SCHEDULED_DURATION],
        handleTextChange(SCHEDULED_DURATION)
      )}
      <Box sx={pc.lineBreak} />
      {textfield(
        pc,
        isDesktop,
        confRestRep[CONFERENCE_NAME],
        translate[CONFERENCE_NAME],
        handleTextChange(CONFERENCE_NAME)
      )}
      {textfield(
        pc,
        isDesktop,
        confRestRep[MAX_PARTICIPANTS],
        translate[MAX_PARTICIPANTS],
        handleTextChange(MAX_PARTICIPANTS)
      )}
      <Box sx={pc.lineBreak} />
      <TextField
        id="standard-name"
        label={translate[HOST_PASSCODE]}
        sx={[pc.textField, isDesktop ? pc.lineExpander : null, readOnlyFieldSx].filter(Boolean)}
        value={confRestRep[HOST_PASSCODE] || ""}
        margin="normal"
        slotProps={{
          input: {
            readOnly: true,
          },
        }}
      />
      <TextField
        id="standard-name"
        label={translate[GUEST_PASSCODE]}
        sx={[pc.textField, isDesktop ? pc.lineExpander : null, readOnlyFieldSx].filter(Boolean)}
        value={confRestRep[GUEST_PASSCODE] || ""}
        margin="normal"
        slotProps={{
          input: {
            readOnly: true,
          },
        }}
      />
      <Box sx={pc.lineBreak} />
      <Box sx={centererButtonSx}>
        <Button
          key="modPin-button"
          variant="contained"
          sx={pc.button}
          onClick={() => requestNewPin(confRestRep.conferenceId, "MODERATOR_PIN", confRestRep)}
        >
          Generate new Host Passcode
        </Button>
      </Box>
      <Box sx={centererButtonSx}>
        <Button
          key="guestPin-button"
          variant="contained"
          sx={pc.button}
          onClick={() => requestNewPin(confRestRep.conferenceId, "PARTICIPANT_PIN", confRestRep)}
        >
          Generate new Guest Passcode
        </Button>
      </Box>
      <Box sx={pc.lineBreak} />
      {switchControl(
        pc,
        confRestRep[CHAIR_DISCONNECT],
        CHAIR_DISCONNECT,
        translate[CHAIR_DISCONNECT],
        handleSwitchChange(CHAIR_DISCONNECT)
      )}
      <Box sx={pc.lineBreak} />
      <TextField
        id="meetyoo_comment"
        label={translate[COMMENT]}
        multiline
        defaultValue={confRestRep[COMMENT]}
        variant="outlined"
        onChange={handleTextChange(COMMENT)}
      />
      <Box sx={pc.lineBreak} />
    </Box>
  );
};

PMConference.propTypes = {
  confRestRep: PropTypes.object.isRequired,
  selectValues: PropTypes.object.isRequired,
  setConfRestRep: PropTypes.func.isRequired,
  translate: PropTypes.object.isRequired,
  requestNewPin: PropTypes.func.isRequired,
};

export default PMConference;
